import { Fragment } from 'preact';
import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { QuestionSelect } from '../QuestionSelect';
import { Tooltip } from '../Tooltip';
import { AppIcon } from '../AppIcon';
import type { PdfDocumentDefinition, PdfPlacement, PdfValueSource } from '../../../shared/types';
import { PDF_LIMITS } from '../../../shared/pdf/limits';
import { createPlacement } from '../../lib/pdfEditor';
import { PDF_FIELD_TYPE_NAMES, type PdfBindingEvent } from '../../../shared/pdfBindingValidation';
import type { ViewerPage } from './PdfPageViewer';
import { answerOptions, NO_ANSWERS, pdfAnswerChoices } from '../../lib/pdfAnswerChoices';
import { SIGNING_DATES } from '../../lib/pdfSources';
export { placementKindFor } from '../../lib/pdfAnswerChoices';

interface Props { document: PdfDocumentDefinition; page?: ViewerPage;
  /** Start drawing this new field on the PDF; it joins the form once it's drawn. */
  onAdd: (placement: PdfPlacement) => void; disabled?: boolean }

const KINDS = [
  { kind: 'text', icon: 'field-text' },
  { kind: 'checkbox', icon: 'field-checkbox' },
  { kind: 'signature', icon: 'field-signature' },
  { kind: 'date', icon: 'field-date' },
] as const;
/** Every field is one of four types, and is added as one with these four buttons: a text field or a checkbox still to be
 * given an answer, a participant's signature box, or the date signed. A field keeps its type, so only answers of that
 * type can fill it. Pressing one starts drawing the new field on the PDF; it joins the form once it's drawn. */
export function PdfAddKinds({ document, page, onAdd, disabled }: Props) {
  const full = document.placements.length >= PDF_LIMITS.placements, off = !page || full || disabled;
  const add = (kind: PdfPlacement['kind']) => {
    if (off) return;
    const source: PdfPlacement['source'] = kind === 'signature' ? { kind: 'signature', role: 'participant' } : kind === 'date' ? { kind: 'submittedDate' } : { kind: 'unset' };
    onAdd({ ...createPlacement(kind, page!.source.pages[page!.pageIndex]), source });
  };
  return <div class="segmented" role="group" aria-label="Add a field">
    {KINDS.map(({ kind, icon }, index) => {
      // A date field always shows the date signed, so its button says so.
      const name = kind === 'date' ? 'date signed field' : PDF_FIELD_TYPE_NAMES[kind]; return <Fragment key={kind}>
      {index > 0 && <span class="segmented-divider" aria-hidden="true" />}
      <Tooltip text={`Add a ${name}`}><button class="segmented-button add-kind-button" aria-label={`Add a ${name}`} disabled={off} onClick={() => add(kind)}>
        <AppIcon name={icon} /><AddBadge /></button></Tooltip>
    </Fragment>; })}
  </div>;
}

/** A small plus at the top right of an add-field button, the same distance in from its top and right edges on every
 * button, as on SF Symbols' badge.plus symbols. A ring in the control's color behind it keeps it clear of the icon. */
function AddBadge() {
  return <svg class="add-badge" viewBox="0 0 12 12" aria-hidden="true">
    <path d="M6 2v8M2 6h8" stroke="var(--accent-soft)" stroke-width="5.5" stroke-linecap="round" />
    <path d="M6 2v8M2 6h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  </svg>;
}

/** In place of the add buttons while a new field is being drawn: what to do, and a way out. Escape cancels too. */
export function PdfPlacingPrompt({ kind, onCancel }: { kind: PdfPlacement['kind']; onCancel: () => void }) {
  return <div class="row pdf-placing" role="status">
    <p><strong>Drag to draw the {PDF_FIELD_TYPE_NAMES[kind]}</strong> or click to place it</p>
    <button type="button" class="secondary" onClick={onCancel}>Cancel</button>
  </div>;
}

/** Who signs a signature box: the two answers it can show. */
const SIGNERS: { label: string; source: PdfValueSource }[] = [
  { label: 'Participant signature', source: { kind: 'signature', role: 'participant' } },
  { label: 'Parent or guardian signature', source: { kind: 'signature', role: 'parent' } },
];
/** Over the PDF, before a new field is drawn: the answer it will show, chosen first — from a dropdown, for a signature
 * field between the participant's and a parent or guardian's signature, and for a date field between the date signed and a parent or guardian's. Place is ready once one is chosen, and then the
 * field is drawn on the PDF. Cancel or Escape adds nothing. */
export function PdfBindAnswer({ event, document, kind, onPlace, onCancel }: { event: PdfBindingEvent; document: PdfDocumentDefinition; kind: PdfPlacement['kind'];
  onPlace: (source: PdfValueSource) => void; onCancel: () => void }) {
  // A date field shows the date signed unless the parent or guardian's date is chosen, so Place is ready at once.
  const choices = kind === 'signature' ? SIGNERS : kind === 'date' ? SIGNING_DATES : pdfAnswerChoices(event, document, kind);
  const [chosen, setChosen] = useState(kind === 'date' ? '0' : ''), title = useId(), box = useRef<HTMLDivElement>(null);
  // It opens with the first control focused, so the keyboard goes straight to choosing.
  useEffect(() => { box.current?.querySelector<HTMLElement>('button.question-type-trigger, input[type=radio], button:not(.secondary)')?.focus({ preventScroll: true }); }, []);
  return <div class="pdf-bind-cover">
    <div ref={box} class="pdf-bind-popover stack" role="dialog" aria-modal="true" aria-labelledby={title}
      onKeyDown={e => {
        if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); e.stopPropagation(); onCancel(); return; }
        // Tab stays within the popover until it's closed.
        if (e.key !== 'Tab') return;
        const stops = [...box.current!.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)')].filter(el => !(el instanceof HTMLInputElement && el.type === 'radio' && !el.checked && box.current!.querySelector('input[type=radio]:checked')));
        const at = stops.indexOf(globalThis.document.activeElement as HTMLElement), last = stops.length - 1;
        if (e.shiftKey ? at <= 0 : at === last || at < 0) { e.preventDefault(); stops[e.shiftKey ? last : 0]?.focus(); }
      }}>
      <h3 id={title}>{kind === 'signature' ? 'Choose a signature type' : kind === 'date' ? 'Choose a date' : `Choose a question to bind to this ${PDF_FIELD_TYPE_NAMES[kind]}`}</h3>
      {kind === 'signature' || kind === 'date'
        ? <div class="stack tight pdf-bind-choices" role="radiogroup" aria-labelledby={title}>{choices.map(({ label }, index) =>
          <label key={label} class="check"><input type="radio" name={`${title}-signer`} checked={chosen === String(index)} onChange={() => setChosen(String(index))} />{label}</label>)}</div>
        : <QuestionSelect label="Answer for the new field" menuWidth="trigger" value={chosen} placeholder="Choose an answer" options={answerOptions(choices as ReturnType<typeof pdfAnswerChoices>)} empty={NO_ANSWERS[kind]} onChange={setChosen} />}
      <div class="row pdf-bind-actions">
        <button type="button" class="secondary" onClick={onCancel}>Cancel</button>
        <button type="button" disabled={chosen === ''} onClick={() => onPlace(choices[Number(chosen)].source)}>Place</button>
      </div>
    </div>
  </div>;
}
